import React, { ComponentProps, memo, ReactNode } from 'react'
import { ToastContentProps } from 'react-toastify'

import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'
import { Checkmark, Cross, Info, SvgIcon, Warning } from '@surglogs-app/icons'
import { BoxIcon, Colors, FlexBox, Icon, Text, Title } from '@surglogs-app/ui'
import { toAlphaColor } from '@surglogs-app/ui.theme'
import { isTransElement } from '@surglogs-app/util'

export const TOAST_WIDTH = '480px'

export type ToastVariant = 'success' | 'danger' | 'info' | 'warning'

const VariantMap: Record<
  ToastVariant,
  { bgc: Colors; iconBgc: Colors; icon: SvgIcon }
> = {
  success: {
    bgc: 'green.10',
    iconBgc: 'green.100',
    icon: Checkmark,
  },
  danger: {
    bgc: 'red.10',
    iconBgc: 'red.100',
    icon: Cross,
  },
  info: {
    bgc: 'blue.10',
    iconBgc: 'blue.100',
    icon: Info,
  },
  warning: {
    bgc: 'orange.10',
    iconBgc: 'orange.100',
    icon: Warning,
  },
}

const ToastContainer = styled(FlexBox)<{ variant: ToastVariant }>`
  background: ${({ theme, variant }) =>
    toAlphaColor(theme.ui.colors[VariantMap[variant].bgc], 0.9)};
  backdrop-filter: blur(${({ theme }) => theme.ui.spacings['abs.10']});

  @media (max-width: ${TOAST_WIDTH}) {
    border-radius: 0;
  }
`

const ToastTextsContainer = styled(FlexBox)<{ titleOnly: boolean }>`
  margin-block: ${({ titleOnly }) => (titleOnly ? 'auto' : 0)};
`

const CloseIcon = styled(Icon)`
  margin-left: auto;
  cursor: pointer;
`

export interface ToastProps
  extends Omit<
    ComponentProps<typeof ToastContainer>,
    'variant' | 'title' | 'children'
  > {
  variant?: ToastVariant
  title: string | ReactNode
  text?: string | ReactNode
  onClose?: () => void
}

export const Toast = memo(
  ({
    variant = 'success',
    text,
    onClose: onCloseProp,
    title,
    closeToast,
    ...rest
  }: ToastProps & Partial<ToastContentProps>) => {
    const onClose = useHandler(() => {
      closeToast?.(true)
      onCloseProp?.()
    })

    const titleOnly = !text

    return (
      <ToastContainer
        {...{
          padding: 'md',
          bra: 'md',
          gap: 'md',
          w: '100%',
          variant,
          ...rest,
        }}>
        <BoxIcon
          {...{
            size: 'xxl',
            iconColor: 'white.100',
            icon: VariantMap[variant].icon,
            variant: VariantMap[variant].iconBgc,
            fsh: 0,
          }}
        />
        <ToastTextsContainer {...{ fd: 'column', gap: 'xs', titleOnly }}>
          {title ? <Title {...{ size: 'xs' }}>{title}</Title> : null}
          {(typeof text === 'string' || isTransElement(text)) && text ? (
            <Text {...{ size: 'xxs' }}>{text}</Text>
          ) : (
            text
          )}
        </ToastTextsContainer>
        <CloseIcon
          {...{
            icon: Cross,
            onClick: onClose,
            'aria-label': 'Close alert',
            role: 'button',
            fsh: 0,
            my: 'sm',
          }}
        />
      </ToastContainer>
    )
  },
)
